import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clearCart } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {}
      if (attempts.current < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="border border-border p-12 max-w-md w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-primary" size={40} />
            <h1 className="font-display font-bold text-2xl mt-6">Confirming payment...</h1>
            <p className="text-sm text-muted-foreground mt-2">Hold tight, verifying with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-green-600" size={40} />
            <h1 data-testid="payment-confirmed-heading" className="font-display font-bold text-2xl mt-6">Order confirmed.</h1>
            <p className="text-sm text-muted-foreground mt-2">Your gear is on the way. Your investment stays protected.</p>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="mx-auto text-primary" size={40} />
            <h1 className="font-display font-bold text-2xl mt-6">
              {status === "timeout" ? "Still processing" : "Payment issue"}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {status === "timeout"
                ? "Payment is taking longer than expected. You'll receive an email confirmation from Stripe."
                : "We couldn't confirm this payment."}
            </p>
          </>
        )}
        <Link to="/shop" data-testid="back-to-store-link" className="inline-block mt-8 bg-foreground text-white px-6 py-3 text-sm uppercase tracking-wider hover:bg-primary transition-colors">
          Back to Store
        </Link>
      </div>
    </div>
  );
}
